import { APP_SCORE_COLORS, ESkillLevel, FEATURE_FLAGS, SKILL_LEVELS } from '@/constants';
import { containerQuery } from '../SkillLevelScale.styles';
import { getSkillLevel, normalizeSkillLevel } from '@/domains';
import { TScoreSkillLevel } from '@/types';
import { useCallback, useMemo } from 'react';
import { useContainerQuery } from 'react-container-query';
import { UseSkillLevelScaleParams, UseSkillLevelScaleReturn } from '../types';

/**
 * Computes all derived state needed to render the skill level scale.
 *
 * Notes:
 * - `score` takes precedence over `level` when both are provided.
 * - Divider visibility is derived from the computed `fillWidth`.
 */
export const useSkillLevelScale = ({
  level,
  runtimeRatingCutoffs,
  score,
  useGradient = true,
}: UseSkillLevelScaleParams): UseSkillLevelScaleReturn => {
  const isV2 = FEATURE_FLAGS.ENABLE_SCORECARD_V2;

  // ================================
  // Container query bindings
  // ================================
  const [containerParams, containerRef] = useContainerQuery(containerQuery, {
    width: 0,
    height: 0,
  });

  // ================================
  // Memoized values
  // ================================

  // Ordered labels used by the scale (level 1 -> expert)
  const skillLevels = useMemo(() => {
    return Object.values(ESkillLevel).map((v) => {
      return normalizeSkillLevel(v);
    }) as unknown as TScoreSkillLevel[];
  }, []);

  // Length of the skill levels array for easy access in calculations
  const skillLevelsLength = skillLevels.length;

  // Resolve the active level, prioritizing numeric score when available
  const currentLevel = useMemo(
    () => (score !== undefined ? getSkillLevel(score, runtimeRatingCutoffs) : level),
    [score, level, runtimeRatingCutoffs],
  );

  // Build the fill background based on the current level
  const gradientBackground = useMemo(() => {
    if (!useGradient) return undefined;

    // Color aliases for cleaner gradient templates
    const c = {
      1: APP_SCORE_COLORS['L1'],
      2: APP_SCORE_COLORS['L2'],
      3: APP_SCORE_COLORS['L3'],
      4: APP_SCORE_COLORS['L4'],
    };

    switch (currentLevel) {
      case isV2 ? SKILL_LEVELS.AWARENESS : SKILL_LEVELS.BEGINNER:
        return `linear-gradient(90deg, ${c[1]} 0%, ${c[1]} 93.27%)`;
      case isV2 ? SKILL_LEVELS.SKILLED : SKILL_LEVELS.INTERMEDIATE:
        return `linear-gradient(90deg, ${c[1]} 0%, ${c[1]} 36.06%, ${c[2]} 93.27%)`;
      case SKILL_LEVELS.ADVANCED:
        return `linear-gradient(90deg, ${c[1]} 0%, ${c[2]} 36.06%, ${c[3]} 93.27%)`;
      case SKILL_LEVELS.EXPERT:
        return `linear-gradient(90deg, ${c[1]} 0%, ${c[2]} 36.06%, ${c[3]} 62.98%, ${c[4]} 93.27%)`;
      default:
        return '';
    }
  }, [currentLevel, isV2, useGradient]);

  // Position of the resolved level in the ordered level array
  const activeIndex = useMemo(() => {
    return currentLevel ? skillLevels.indexOf(currentLevel) : -1;
  }, [currentLevel, skillLevels]);

  // Convert either score or active level into a 0-100 fill percentage
  const fillWidth = useMemo(() => {
    if (score !== undefined && score <= skillLevelsLength) {
      return (score / skillLevelsLength) * 100;
    }

    return activeIndex >= 0 ? ((activeIndex + 1) / skillLevelsLength) * 100 : 0;
  }, [score, activeIndex, skillLevelsLength]);

  // ================================
  // Callbacks
  // ================================

  // Determine whether to show or hide dividers based on if they would sit under the fill bar edge
  const getDividerDisplay = useCallback(
    (index: number): 'none' | 'block' => {
      // Hide divider that sits exactly at the fill edge to avoid visual overlap
      if (fillWidth === 25 && index === 0) {
        return 'none';
      }

      if (fillWidth === 50 && index === 1) {
        return 'none';
      }

      if (fillWidth === 75 && index === 2) {
        return 'none';
      }

      return 'block';
    },
    [fillWidth],
  );

  return {
    activeIndex,
    containerParams,
    containerRef,
    currentLevel,
    fillWidth,
    getDividerDisplay,
    gradientBackground,
    skillLevels,
    skillLevelsLength,
  };
};

export default useSkillLevelScale;
